import { create } from 'zustand';
import {
  Event,
  ServerMessage,
  GameStateData,
  QuestionOpenData,
  QueueEntry,
  ScoreEntry,
  AnswerResultData,
  CalledOnData,
  WelcomeData,
  ErrorData,
  VoteTally,
  VoteChoice,
} from './shared/protocol';
import { updatePlayerSession } from './session';

interface GameStore {
  connected: boolean;
  state: GameStateData | null;
  playerId: string | null;
  calledOn: CalledOnData | null;
  lastResult: AnswerResultData | null;
  error: string | null;
  /** normcringe: this device's vote on the current statement (client-local; lost on reload) */
  myVote: VoteChoice | null;

  setConnected: (c: boolean) => void;
  setMyVote: (v: VoteChoice) => void;
  apply: (msg: ServerMessage) => void;
  clearError: () => void;
  reset: () => void;
}

export const useGameStore = create<GameStore>((set) => ({
  connected: false,
  state: null,
  playerId: null,
  calledOn: null,
  lastResult: null,
  error: null,
  myVote: null,

  setConnected: (connected) => set({ connected }),
  setMyVote: (myVote) => set({ myVote }),
  clearError: () => set({ error: null }),
  reset: () => set({ state: null, calledOn: null, lastResult: null, error: null, myVote: null }),

  apply: (msg) =>
    set((s) => {
      switch (msg.type) {
        case Event.GameState: {
          const next = msg.data as GameStateData;
          // Clear the called-on / answer-result banners when we move to a
          // different question or re-arm one (idle) — but keep them while the
          // same question is merely resolved (closed) so the reveal stays visible.
          const questionChanged = next.question?.id !== s.state?.question?.id;
          const cleared = questionChanged || next.questionState === 'idle';
          return {
            state: next,
            calledOn: cleared ? null : s.calledOn,
            lastResult: cleared ? null : s.lastResult,
            myVote: cleared ? null : s.myVote,
          };
        }

        case Event.Welcome: {
          const d = msg.data as WelcomeData;
          // Persist identity so reconnects re-attach to the same player/score.
          updatePlayerSession({ playerId: d.playerId, nickname: d.nickname });
          return { playerId: d.playerId };
        }

        case Event.QuestionOpen: {
          const d = msg.data as QuestionOpenData;
          if (!s.state) return s;
          return {
            state: {
              ...s.state,
              questionState: 'open',
              question: d.question,
              topicTitle: d.topicTitle,
              endsAt: d.endsAt,
              answerNote: d.answerNote ?? s.state.answerNote,
              queue: [],
              votes: undefined,
            },
            calledOn: null,
            lastResult: null,
            myVote: null,
          };
        }

        case Event.QueueUpdate: {
          if (!s.state) return s;
          const queue = msg.data as QueueEntry[];
          // If the buzz shown in the result banner went back to pending, the admin
          // undid that mark — drop the stale banner.
          const undone =
            s.lastResult != null &&
            queue.some((e) => e.playerId === s.lastResult!.playerId && e.result === 'pending');
          return { state: { ...s.state, queue }, lastResult: undone ? null : s.lastResult };
        }

        case Event.QuestionShut:
          if (!s.state) return s;
          return { state: { ...s.state, questionState: 'closed', queue: msg.data as QueueEntry[] } };

        case Event.ScoreUpdate:
          if (!s.state) return s;
          return { state: { ...s.state, scores: msg.data as ScoreEntry[] } };

        case Event.CalledOn:
          return { calledOn: msg.data as CalledOnData };

        case Event.AnswerResult:
          return { lastResult: msg.data as AnswerResultData };

        case Event.GameEnded:
          if (!s.state) return { };
          return { state: { ...s.state, status: 'finished', scores: msg.data as ScoreEntry[] } };

        case Event.VoteUpdate:
          if (!s.state) return s;
          return { state: { ...s.state, votes: msg.data as VoteTally } };

        case Event.VoteReveal:
          if (!s.state) return s;
          return { state: { ...s.state, questionState: 'closed', votes: msg.data as VoteTally } };

        case Event.Error:
          return { error: (msg.data as ErrorData).message };

        default:
          return s;
      }
    }),
}));
